import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    failContract,
    hasOwn,
    isRecord,
    requireArray,
    requireBoolean,
    requireKnownEnum,
    requireNumber,
    requireRecord,
    requireString,
    requireTimezoneAwareDateTime,
} from "./contractValidation";
import { decodeMimeEncodedWords } from "./inboxConversationListViewModel";

dayjs.extend(relativeTime);

const UNKNOWN_SUBJECT = "无主题";

const CATEGORY_LANGUAGE = {
    crew_transport: "机组接送",
    crew_transportation: "机组接送",
    catering: "配餐",
    fuel: "燃油",
    fuel_request: "燃油",
    crew_activation: "机组派遣",
    flight_brief: "飞行简报",
    permit_request: "许可申请",
    hotac: "酒店住宿",
    general: "综合邮件",
};

const STATUS_LANGUAGE = {
    confirmed: { label: "已确认", tone: "success" },
    pending_review: { label: "待审核", tone: "warning" },
    rejected: { label: "已排除", tone: "neutral" },
    unknown: { label: "状态待核对", tone: "neutral" },
    conflict: { label: "关联冲突", tone: "danger" },
    conflicted: { label: "关联冲突", tone: "danger" },
};

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function toRecord(value) {
    return isRecord(value) ? value : {};
}

function normalizeWhitespace(value) {
    return String(value).replace(/\s+/g, " ").trim();
}

function normalizeText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    const decoded = normalizeWhitespace(decodeMimeEncodedWords(String(value)));
    return decoded || fallback;
}

function buildTime(value, path) {
    const parsed = requireTimezoneAwareDateTime(value, path);
    return {
        value: parsed,
        timestamp: parsed.valueOf(),
        relativeText: parsed.locale("zh-cn").fromNow(),
        absoluteText: parsed.format("YYYY-MM-DD HH:mm"),
    };
}

function formatDateRange(firstDate, latestDate, path, { allowEmpty = false } = {}) {
    if (allowEmpty && !firstDate && !latestDate) return "无可见邮件跨度";
    if (!firstDate || !latestDate) {
        failContract("响应契约错误", `${path} 缺少完整时间跨度`);
    }
    const first = buildTime(firstDate, `${path}.earliest`);
    const latest = buildTime(latestDate, `${path}.latest`);
    if (first.absoluteText === latest.absoluteText) return latest.absoluteText;
    return `${first.absoluteText} 至 ${latest.absoluteText}`;
}

function formatCategory(category) {
    const key = normalizeText(category).toLowerCase();
    return CATEGORY_LANGUAGE[key] || "其他邮件";
}

function formatCategoryList(categories) {
    const labels = Array.from(new Set(toArray(categories).map(formatCategory).filter(Boolean)));
    return labels.length > 0 ? labels : ["综合邮件"];
}

function mapStatus(status) {
    return requireKnownEnum(status, STATUS_LANGUAGE, "thread.product_status").value;
}

function countEvidenceRefs(businessLinks) {
    return toArray(businessLinks).reduce((total, link) => {
        const evidenceRefs = link?.evidence_refs;
        if (Array.isArray(evidenceRefs)) return total + evidenceRefs.length;
        if (evidenceRefs && typeof evidenceRefs === "object") return total + Object.keys(evidenceRefs).length;
        return total;
    }, 0);
}

function buildThreadEvidence(thread = {}) {
    const attachmentCount = requireNumber(thread.attachment_count, "thread.attachment_count", { integer: true, min: 0 });
    const inlineImageCount = requireNumber(thread.inline_image_count, "thread.inline_image_count", { integer: true, min: 0 });
    const evidenceRefCount = countEvidenceRefs(thread.business_links);
    const items = [];

    if (attachmentCount > 0) {
        items.push(`${attachmentCount} 个附件`);
    }
    if (inlineImageCount > 0) {
        items.push(`${inlineImageCount} 张内嵌图片`);
    }
    if (evidenceRefCount > 0) {
        items.push(`${evidenceRefCount} 条证据引用`);
    }

    return {
        attachmentCount,
        inlineImageCount,
        evidenceRefCount,
        hasEvidence: items.length > 0,
        label: items.length > 0 ? items.join("、") : "进入详情核对原文",
    };
}

function buildParticipants(participants) {
    const labels = toArray(participants)
        .map((item) => normalizeText(item))
        .filter(Boolean);
    if (labels.length === 0) return ["参与方未标明"];
    return labels;
}

function buildThreadItem(thread = {}, index = 0) {
    const record = requireRecord(thread, `categories[].threads[${index}]`);
    const threadId = normalizeText(record.thread_id);
    const receiptId = normalizeText(record.primary_receipt_id);
    if (!threadId && !receiptId) {
        failContract("响应契约错误", `categories[].threads[${index}] 缺少可打开的 thread_id 或 primary_receipt_id`);
    }
    const participants = buildParticipants(thread.participants);
    const categories = formatCategoryList(thread.categories?.length ? thread.categories : [thread.primary_category]);
    const evidence = buildThreadEvidence(record);
    const latestTime = buildTime(record.latest_date, `categories[].threads[${index}].latest_date`);

    return {
        key: normalizeText(thread.thread_key || threadId || receiptId, String(index)),
        threadId,
        receiptId,
        subject: normalizeText(thread.subject || thread.latest_subject, UNKNOWN_SUBJECT),
        latestSummary: normalizeText(thread.latest_summary || thread.work_item?.result_summary),
        participants,
        participantText: participants.slice(0, 3).join("、"),
        participantOverflowCount: Math.max(participants.length - 3, 0),
        timeRangeText: formatDateRange(record.first_date, record.latest_date, `categories[].threads[${index}].date_range`),
        latestTime,
        messageCount: requireNumber(record.message_count, `categories[].threads[${index}].message_count`, { integer: true, min: 0 }),
        linkCount: requireNumber(record.link_count, `categories[].threads[${index}].link_count`, { integer: true, min: 0 }),
        categories,
        status: mapStatus(record.product_status),
        evidence,
        canOpen: Boolean(threadId || receiptId),
    };
}

function buildThreads(categories) {
    return requireArray(categories, "entityDigest.categories")
        .flatMap((category, categoryIndex) => requireArray(requireRecord(category, `entityDigest.categories[${categoryIndex}]`).threads, `entityDigest.categories[${categoryIndex}].threads`))
        .map(buildThreadItem)
        .filter((item) => item.key)
        .sort((left, right) => right.latestTime.timestamp - left.latestTime.timestamp);
}

function isAgReviewCasesUnavailable(agReviewCases) {
    return agReviewCases?.available === false;
}

function getAgReviewUnavailableDescription(agReviewCases) {
    const reason = normalizeText(agReviewCases?.unavailable_reason || agReviewCases?.reason);
    if (reason === "permission_denied" || reason === "forbidden" || reason === "no_permission") {
        return "当前账号无权查看 AG 审核投影，不能据此判断没有审核事项。";
    }
    return reason || "AG 审核投影暂不可用，不能据此判断没有审核事项。";
}

function validateStatusCounts(statusCounts) {
    Object.entries(statusCounts).forEach(([statusKey, count]) => {
        requireNumber(count, `entityDigest.summary.status_counts.${statusKey}`, { integer: true, min: 0 });
        if (hasOwn(STATUS_LANGUAGE, statusKey)) return;
        failContract("响应契约错误", `entityDigest.summary.status_counts 返回未知状态 ${statusKey}`);
    });
}

function buildSummary(summary = {}, agReviewCases = {}) {
    const record = requireRecord(summary, "entityDigest.summary");
    const statusCounts = requireRecord(record.status_counts, "entityDigest.summary.status_counts");
    validateStatusCounts(statusCounts);
    const totalThreads = requireNumber(record.total_threads, "entityDigest.summary.total_threads", { integer: true, min: 0 });
    const dateRange = totalThreads > 0
        ? requireRecord(record.date_range, "entityDigest.summary.date_range")
        : toRecord(record.date_range);
    const reviewCases = requireRecord(agReviewCases, "entityDigest.ag_review_cases");
    const agReviewUnavailable = isAgReviewCasesUnavailable(reviewCases);
    const pendingReviewCount = agReviewUnavailable
        ? "不可用"
        : requireNumber(reviewCases.pending_count, "entityDigest.ag_review_cases.pending_count", { integer: true, min: 0 });
    const unknownReviewCount = agReviewUnavailable
        ? null
        : requireNumber(reviewCases.unknown_count, "entityDigest.ag_review_cases.unknown_count", { integer: true, min: 0 });
    const conflictAvailable = hasOwn(statusCounts, "conflict") || hasOwn(statusCounts, "conflicted");

    return {
        totalMessages: requireNumber(record.total_messages, "entityDigest.summary.total_messages", { integer: true, min: 0 }),
        totalThreads,
        totalLinks: requireNumber(record.total_links, "entityDigest.summary.total_links", { integer: true, min: 0 }),
        confirmedCount: requireNumber(statusCounts.confirmed, "entityDigest.summary.status_counts.confirmed", { integer: true, min: 0 }),
        pendingReviewCount,
        rejectedCount: requireNumber(statusCounts.rejected, "entityDigest.summary.status_counts.rejected", { integer: true, min: 0 }),
        unknownCount: requireNumber(statusCounts.unknown ?? 0, "entityDigest.summary.status_counts.unknown", { integer: true, min: 0 }),
        conflictCount: conflictAvailable ? requireNumber(statusCounts.conflict ?? statusCounts.conflicted, "entityDigest.summary.status_counts.conflict", { integer: true, min: 0 }) : null,
        conflictAvailable,
        unknownReviewCount,
        latestUpdated: totalThreads > 0 ? buildTime(dateRange.latest, "entityDigest.summary.date_range.latest") : null,
        dateRangeText: formatDateRange(dateRange.earliest, dateRange.latest, "entityDigest.summary.date_range", { allowEmpty: totalThreads === 0 }),
    };
}

function buildCategoryChips(categoryCounts = {}) {
    return Object.entries(requireRecord(categoryCounts, "entityDigest.summary.category_counts"))
        .map(([category, count]) => ({
            key: category,
            label: formatCategory(category),
            count: requireNumber(count, `entityDigest.summary.category_counts.${category}`, { integer: true, min: 0 }),
        }))
        .filter((item) => item.count > 0)
        .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, "zh-CN"));
}

function buildReviewPrompt(agReviewCases = {}) {
    const collection = requireRecord(agReviewCases, "entityDigest.ag_review_cases");
    if (isAgReviewCasesUnavailable(collection)) {
        return {
            visible: true,
            unavailable: true,
            pendingCount: null,
            sourceCount: null,
            unknownCount: null,
            pendingReceiptCount: null,
            title: "AG 审核数据不可用",
            description: getAgReviewUnavailableDescription(collection),
        };
    }

    const pendingCount = requireNumber(collection.pending_count, "entityDigest.ag_review_cases.pending_count", { integer: true, min: 0 });
    const sourceCount = requireNumber(collection.source_count, "entityDigest.ag_review_cases.source_count", { integer: true, min: 0 });
    const unknownCount = requireNumber(collection.unknown_count, "entityDigest.ag_review_cases.unknown_count", { integer: true, min: 0 });
    const pendingReceiptCount = requireNumber(collection.pending_receipt_count, "entityDigest.ag_review_cases.pending_receipt_count", { integer: true, min: 0 });
    if (unknownCount > 0) {
        failContract("响应契约错误", "entityDigest.ag_review_cases.unknown_count 表示存在未知审核生命周期");
    }
    const latest = toRecord(collection.latest);
    const latestSummary = normalizeText(latest.review_case_summary || latest.result_summary);

    return {
        visible: pendingCount > 0 || sourceCount > 0 || unknownCount > 0,
        pendingCount,
        sourceCount,
        unknownCount,
        pendingReceiptCount,
        title: pendingCount > 0 ? `${pendingCount} 项关联判断待审核` : "已有审核记录",
        description: latestSummary || (pendingCount > 0 ? "需要人工确认邮件与当前业务对象的关联。" : "当前对象已有审核历史，可进入邮件阅读面核对上下文。"),
    };
}

function buildEvidencePrompt(threads) {
    const attachmentCount = threads.reduce((total, thread) => total + thread.evidence.attachmentCount, 0);
    const evidenceRefCount = threads.reduce((total, thread) => total + thread.evidence.evidenceRefCount, 0);
    const evidenceThreadCount = threads.filter((thread) => thread.evidence.hasEvidence).length;

    return {
        visible: threads.length > 0,
        attachmentCount,
        evidenceRefCount,
        evidenceThreadCount,
        description: evidenceThreadCount > 0
            ? `${evidenceThreadCount} 个会话带有附件或证据引用，点击会话后可在阅读面核对原文。`
            : "当前摘要未返回附件或证据引用，点击会话后可在阅读面核对原文。",
    };
}

function mapEmptyState(emptyState = null) {
    const reason = requireString(requireRecord(emptyState, "entityDigest.empty_state").reason, "entityDigest.empty_state.reason").toLowerCase();
    if (reason === "no_accessible_mailboxes") {
        return "当前账号没有可查看的邮件范围。";
    }
    if (reason === "no_visible_mail_hub_business_links_for_filter" || reason === "no_threads_match_filter") {
        return "当前业务对象暂无可见关联邮件。";
    }
    failContract("响应契约错误", `entityDigest.empty_state.reason 返回未知空态原因 ${reason}`);
}

function validateDataSource(payload) {
    const dataSource = requireRecord(payload.data_source, "entityDigest.data_source");
    if (dataSource.business_links !== "mail_hub.business_link") {
        failContract("响应契约错误", "entityDigest.data_source.business_links 必须为 mail_hub.business_link");
    }
    if (requireBoolean(dataSource.raw_email_business_links_included, "entityDigest.data_source.raw_email_business_links_included") !== false) {
        failContract("响应契约错误", "Entity Digest 不能混入旧 raw_email_business_link 数据源");
    }
}

export function buildEntityEmailDigestViewModel(payload) {
    const record = requireRecord(payload, "entityDigest");
    validateDataSource(record);
    const agReviewCases = requireRecord(record.ag_review_cases, "entityDigest.ag_review_cases");
    const summary = buildSummary(record.summary, agReviewCases);
    const threads = buildThreads(record.categories);
    const pagination = requireRecord(record.pagination, "entityDigest.pagination");
    if (threads.length === 0 && !hasOwn(record, "empty_state")) {
        failContract("响应契约错误", "Entity Digest 未返回 threads 时必须提供 empty_state");
    }

    return {
        entity: {
            type: requireString(record.entity_type, "entityDigest.entity_type"),
            id: requireString(record.entity_id, "entityDigest.entity_id"),
        },
        summary,
        agReview: {
            available: !isAgReviewCasesUnavailable(agReviewCases),
            unavailableReason: isAgReviewCasesUnavailable(agReviewCases) ? getAgReviewUnavailableDescription(agReviewCases) : "",
        },
        categoryChips: buildCategoryChips(requireRecord(record.summary, "entityDigest.summary").category_counts),
        threads,
        reviewPrompt: buildReviewPrompt(agReviewCases),
        evidencePrompt: buildEvidencePrompt(threads),
        pagination: {
            page: requireNumber(pagination.page, "entityDigest.pagination.page", { integer: true, min: 1 }),
            pageSize: requireNumber(pagination.page_size, "entityDigest.pagination.page_size", { integer: true, min: 1 }),
            totalThreads: requireNumber(pagination.total_threads, "entityDigest.pagination.total_threads", { integer: true, min: 0 }),
            hasMore: requireBoolean(pagination.has_more, "entityDigest.pagination.has_more"),
        },
        empty: {
            visible: threads.length === 0,
            message: threads.length === 0 ? mapEmptyState(record.empty_state) : "",
        },
    };
}
